import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, RadioGroup, Radio, FormControlLabel, FormGroup,
    Checkbox, TextField, useTheme, Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../../../../theme';
import FlagButton from './FlagButton';
import QuestionRender from '../../../../../components/QuestionRender';
import { useSnackbar } from 'notistack';
import v2Api from '../../../../../utils/v2Api';
import { mergeUploadedUrls } from './imageUploadHelpers';

/**
 * QuestionSlot — renders a single question for test-taking or results viewing.
 *
 * Props:
 *   question           - question object from test structure
 *   allQuestionsInSection - all questions in current section (for choice groups)
 *
 * Test-taking mode (default):
 *   answer, setAnswer, toggleFlag, choiceGroupSelections, setChoiceGroupSelection
 *     — passed directly or via useTestSession context
 *
 * Results mode (readOnly=true):
 *   readOnly            - boolean
 *   studentAnswer       - { answer_text, flagged, is_correct, points_earned }
 *   partScoreById       - { [partId]: { score, max_score, grading_status, feedback_text } }
 *   isAdmin             - show edit controls
 *   editingPart, setEditingPart, onSavePartScore - admin score editing
 */
const QuestionSlot = ({
    question, allQuestionsInSection = [],
    // Test-taking props (optional — uses context if not provided)
    answer: answerProp, setAnswer: setAnswerProp, toggleFlag: toggleFlagProp,
    choiceGroupSelections: cgsProp, setChoiceGroupSelection: setCgsProp,
    // Results props
    readOnly = false, studentAnswer, partScoreById = {}, wholeQuestionScoreByQid = {},
    isAdmin = false, revealCorrectAnswers = false, editingPart, setEditingPart, onSavePartScore,
    // blankIsPriced — the test's scoring profile pays for a blank (AMC: +1.5 vs 0 for wrong). A
    // blank is stored as "not correct" like a wrong answer, so without this it would wear the same
    // red icon as a wrong answer while being scored differently. Other tests keep the red icon.
    blankIsPriced = false,
    // Layout
    verticalOptions = false,
    noBorder = false,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { enqueueSnackbar } = useSnackbar();
    const [uploadingParts, setUploadingParts] = useState({});
    // Which dropzone (by uploadKey) is currently being dragged over.
    // A per-key counter ref handles enter/leave on child elements without flicker.
    const [draggingPart, setDraggingPart] = useState(null);
    const dragCountRef = useRef({});
    const MAX_IMAGES_PER_PART = 5;
    const MAX_FILE_SIZE_MB = 10;
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];

    // In test-taking mode, get from context if not passed as props.
    // sessionReadOnly = the session itself is review-mode (teacher review):
    // inputs render as in taking mode but mutators are no-ops, and the
    // pre-filled answers get the green "this is the key" treatment.
    let answer, setAnswer, toggleFlag, choiceGroupSelections, setChoiceGroupSelection, sessionId, frqSubmissionMethod;
    let sessionReadOnly = false;
    if (!readOnly) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ctx = require('../TestSessionContext').useTestSession();
        answer = answerProp || ctx.answers.get(question.id) || { answer_text: '', flagged: false };
        setAnswer = setAnswerProp || ctx.setAnswer;
        toggleFlag = toggleFlagProp || ctx.toggleFlag;
        choiceGroupSelections = cgsProp || ctx.choiceGroupSelections;
        setChoiceGroupSelection = setCgsProp || ctx.setChoiceGroupSelection;
        sessionId = ctx.session?.id;
        sessionReadOnly = !!ctx.readOnly;
        frqSubmissionMethod = ctx.test?.metadata?.frq_submission_method || 'text';
    } else {
        answer = studentAnswer || { answer_text: '', flagged: false };
        // Results view: accept selections via prop so we can hide non-selected choice-group questions.
        // If no selections are provided (legacy sessions), fall through to showing everything.
        choiceGroupSelections = cgsProp || null;
        frqSubmissionMethod = 'text'; // results view doesn't need this
    }

    // Track the latest answer state in a ref so async upload completions can
    // merge into post-removal state instead of the stale closure capture.
    const answerRef = useRef(answer);
    useEffect(() => { answerRef.current = answer; }, [answer]);

    const questionType = question.question_type || '';
    const isMcqType = questionType === 'mcq' || questionType === 'multi_answer' || questionType === 'grid_in' || questionType === 'sa';

    // Choice group logic
    const isInChoiceGroup = !!question.choice_group;
    const hasSelectionsMap = choiceGroupSelections && typeof choiceGroupSelections === 'object' && Object.keys(choiceGroupSelections).length > 0;
    const selectedInThisGroup = isInChoiceGroup ? choiceGroupSelections?.[question.choice_group] : null;
    const isSelectedInGroup =
        !isInChoiceGroup ||
        selectedInThisGroup === question.id ||
        // No recorded selection for THIS group — show everything in it (matches server behavior).
        (readOnly && (selectedInThisGroup === undefined || selectedInThisGroup === null)) ||
        (readOnly && !hasSelectionsMap); // legacy readOnly sessions with no persisted selections at all
    const isGroupUndecided = !readOnly && isInChoiceGroup && selectedInThisGroup === undefined;
    const isHiddenByGroup = isInChoiceGroup && !isSelectedInGroup && !isGroupUndecided;

    const isFirstInGroup = !readOnly && isInChoiceGroup &&
        allQuestionsInSection.filter(q => q.choice_group === question.choice_group)[0]?.id === question.id;
    const groupQuestions = isFirstInGroup
        ? allQuestionsInSection.filter(q => q.choice_group === question.choice_group) : [];

    const handleMcqChange = (e) => {
        if (readOnly) return;
        if (question.is_multi_select) {
            const current = answer.answer_text ? answer.answer_text.split(',') : [];
            const val = e.target.value;
            const next = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
            setAnswer(question.id, next.sort().join(','));
        } else {
            setAnswer(question.id, e.target.value);
        }
    };

    // ─── Render: Choice Group Selector (test-taking only) ─────
    const renderChoiceGroupSelector = () => {
        if (readOnly || !isFirstInGroup) return null;
        return (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: colors.blueAccent[400], borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold" mb={1}>Choose one question to answer:</Typography>
                <RadioGroup
                    row
                    value={choiceGroupSelections[question.choice_group] ?? ''}
                    onChange={(e) => setChoiceGroupSelection(question.choice_group, parseInt(e.target.value, 10))}
                >
                    {groupQuestions.map(gq => (
                        <FormControlLabel key={gq.id} value={gq.id} control={<Radio size="small" />} label={`Q${gq.question_number}`} />
                    ))}
                </RadioGroup>
            </Box>
        );
    };

    // ─── Render: MCQ Input / Result ───────────────────────────
    // Render option label — use QuestionRender if text contains LaTeX, otherwise plain string
    const renderOptionLabel = (opt) => {
        const text = opt.option_text || '';
        const hasLatex = text.includes('\\(') || text.includes('\\[') || text.includes('$');
        if (text && hasLatex) {
            return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{opt.option_label}.</span>
                    <QuestionRender content={text} sx={{ display: 'inline', '& .katex': { fontSize: '1em' } }} />
                </Box>
            );
        }
        return `${opt.option_label}${text ? `. ${text}` : ''}`;
    };

    // Unanswered, or cleared back to nothing: exactly what the server scored as blank.
    const isBlankAnswer = !String(answer?.answer_text ?? '').trim();

    const renderMcq = () => {
        const options = question.v2_question_options || [];

        if (readOnly) {
            return (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2">{isAdmin ? "Student's answer:" : "Your answer:"} <b>{answer.answer_text || '—'}</b></Typography>
                    {(isAdmin || revealCorrectAnswers) && questionType === 'mcq' && question.answer && (
                        <Typography variant="body2" color="text.secondary">Correct: <b>{question.answer}</b></Typography>
                    )}
                    {answer.is_correct === true && <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
                    {answer.is_correct === false && !(blankIsPriced && isBlankAnswer) && <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                    {/* A never-touched question has no answer row (is_correct null) and a cleared one has
                        an empty row (is_correct false); the server scores both as blank, so both read "Blank". */}
                    {answer.is_correct !== true && blankIsPriced && isBlankAnswer && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" color="text.disabled">Blank</Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        // Review mode: the pre-filled selection IS the answer key — paint it green
        const keySx = (isKey) => (isKey ? {
            '& .MuiFormControlLabel-label': { color: '#51BE90', fontWeight: 700 },
        } : {});
        const keyControlSx = (isKey) => (isKey ? { color: '#51BE90', '&.Mui-checked': { color: '#51BE90' } } : {});

        if (question.is_multi_select) {
            const selected = answer.answer_text ? answer.answer_text.split(',') : [];
            return (
                <FormGroup row={!verticalOptions}>
                    {options.map(opt => {
                        const isKey = sessionReadOnly && selected.includes(opt.option_label);
                        return (
                        <FormControlLabel key={opt.option_label} sx={keySx(isKey)}
                            control={<Checkbox checked={selected.includes(opt.option_label)} onChange={handleMcqChange} value={opt.option_label} size="small" sx={keyControlSx(isKey)} />}
                            label={renderOptionLabel(opt)}
                        />
                        );
                    })}
                </FormGroup>
            );
        }
        return (
            <RadioGroup row={!verticalOptions} value={answer.answer_text || ''}>
                {options.map(opt => {
                    const isKey = sessionReadOnly && answer.answer_text === opt.option_label;
                    return (
                    <FormControlLabel key={opt.option_label} value={opt.option_label} sx={keySx(isKey)}
                        control={<Radio size="small" sx={keyControlSx(isKey)} onClick={() => {
                            if (readOnly) return;
                            setAnswer(question.id, answer.answer_text === opt.option_label ? '' : opt.option_label);
                        }} />}
                        label={renderOptionLabel(opt)}
                    />
                    );
                })}
            </RadioGroup>
        );
    };

    // ─── Render: Text Input / Result (multi_answer, grid_in) ──
    const renderTextInput = () => {
        if (readOnly) {
            // Correct answer is shown to admins always; to students only when the results
            // view opts in via revealCorrectAnswers (vocab tests with grades released).
            // Other tests keep the answer key hidden from students. The key lives in
            // accepted_answers (array), falling back to the single `answer` field.
            const correctText = Array.isArray(question.accepted_answers) && question.accepted_answers.length
                ? question.accepted_answers.join(' / ')
                : (question.answer || '');
            return (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{isAdmin ? "Student's answer:" : "Your answer:"} <b>{answer.answer_text || '—'}</b></Typography>
                    {(isAdmin || revealCorrectAnswers) && correctText && (
                        <Typography variant="body2" color="text.secondary">Correct: <b>{correctText}</b></Typography>
                    )}
                    {answer.is_correct === true && <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
                    {answer.is_correct === false && !(blankIsPriced && isBlankAnswer) && <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                    {/* A never-touched question has no answer row (is_correct null) and a cleared one has
                        an empty row (is_correct false); the server scores both as blank, so both read "Blank". */}
                    {answer.is_correct !== true && blankIsPriced && isBlankAnswer && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" color="text.disabled">Blank</Typography>
                        </Box>
                    )}
                </Box>
            );
        }
        return (
            <TextField fullWidth size="small" variant="outlined" placeholder="Type your answer"
                value={answer.answer_text || ''} onChange={(e) => setAnswer(question.id, e.target.value)}
                sx={{ mt: 1, ...(sessionReadOnly && answer.answer_text ? {
                    '& .MuiInputBase-input': { color: '#51BE90', fontWeight: 700 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#51BE90' },
                } : {}) }}
            />
        );
    };

    // ─── FRQ Image Upload Helpers ────────────────────────────
    const handleImageUpload = async (partLabel, files) => {
        const uploadKey = `${question.id}_${partLabel}`;
        // Parse current images from answer
        let currentAnswerObj = {};
        try { currentAnswerObj = answer.answer_text ? JSON.parse(answer.answer_text) : {}; } catch { /* */ }
        const currentImages = Array.isArray(currentAnswerObj[partLabel]) ? currentAnswerObj[partLabel] : [];

        // Some browsers don't recognize image/heic or image/heif on the File.type
        // (Safari fills it in, Chrome/Firefox often leave it blank) — fall back
        // to a filename-extension check so HEIC uploads aren't rejected client-side.
        const hasValidType = (f) =>
            ALLOWED_IMAGE_TYPES.includes(f.type) || /\.hei[cf]$/i.test(f.name);

        const allFiles = Array.from(files);
        const oversizedFiles = allFiles.filter(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        const otherInvalidFiles = allFiles.filter(f => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024 && !hasValidType(f));
        const validFiles = allFiles.filter(f => hasValidType(f) && f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);

        if (oversizedFiles.length > 0) {
            enqueueSnackbar(
                `${oversizedFiles.length} file${oversizedFiles.length > 1 ? 's' : ''} exceeded the ${MAX_FILE_SIZE_MB}MB limit and ${oversizedFiles.length > 1 ? 'were' : 'was'} skipped.`,
                { variant: 'warning', autoHideDuration: 6000 }
            );
        }
        if (otherInvalidFiles.length > 0) {
            enqueueSnackbar(
                `${otherInvalidFiles.length} unsupported file${otherInvalidFiles.length > 1 ? 's' : ''} skipped. Use JPG, PNG, HEIC, or WebP.`,
                { variant: 'warning', autoHideDuration: 6000 }
            );
        }

        const slotsLeft = MAX_IMAGES_PER_PART - currentImages.length;
        const filesToUpload = validFiles.slice(0, Math.max(0, slotsLeft));
        if (filesToUpload.length === 0) return;

        setUploadingParts(prev => ({ ...prev, [uploadKey]: true }));
        try {
            const uploadedUrls = [];
            for (const f of filesToUpload) {
                const formData = new FormData();
                formData.append('file', f);
                formData.append('question_id', String(question.id));
                formData.append('part_label', partLabel);
                try {
                    const res = await v2Api.upload(`/sessions/${sessionId}/frq-image-upload`, formData);
                    uploadedUrls.push(res.data.publicUrl);
                } catch (uploadErr) {
                    const msg = uploadErr.response?.data?.error || 'upload failed';
                    enqueueSnackbar(`Couldn't process ${f.name}: ${msg}`, { variant: 'error', autoHideDuration: 6000 });
                }
            }
            if (uploadedUrls.length > 0) {
                // Read latest answer state (not the closure-captured currentAnswerObj)
                // so a concurrent thumbnail removal or upload to another part isn't undone.
                setAnswer(question.id, mergeUploadedUrls(answerRef.current?.answer_text, partLabel, uploadedUrls));
            }
        } catch (err) {
            console.error('Image upload failed:', err);
        } finally {
            setUploadingParts(prev => ({ ...prev, [uploadKey]: false }));
        }
    };

    const handleImageRemove = (partLabel, index) => {
        let currentAnswerObj = {};
        try { currentAnswerObj = answer.answer_text ? JSON.parse(answer.answer_text) : {}; } catch { /* */ }
        const currentImages = Array.isArray(currentAnswerObj[partLabel]) ? currentAnswerObj[partLabel] : [];
        const updated = { ...currentAnswerObj, [partLabel]: currentImages.filter((_, i) => i !== index) };
        setAnswer(question.id, JSON.stringify(updated));
    };

    const renderUploadDropzone = (partLabel) => {
        const uploadKey = `${question.id}_${partLabel}`;
        const isUploading = uploadingParts[uploadKey];
        let currentAnswerObj = {};
        try { currentAnswerObj = answer.answer_text ? JSON.parse(answer.answer_text) : {}; } catch { /* */ }
        const images = Array.isArray(currentAnswerObj[partLabel]) ? currentAnswerObj[partLabel] : [];

        return (
            <Box sx={{ mt: 1 }}>
                {images.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                        {images.map((url, idx) => (
                            <Box key={idx} sx={{ position: 'relative', width: 80, height: 80, borderRadius: 0.5, overflow: 'hidden', border: '1px solid', borderColor: colors.grey[700] }}>
                                <img src={url} alt={`Upload ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <IconButton size="small" onClick={() => handleImageRemove(partLabel, idx)}
                                    sx={{ position: 'absolute', top: 1, right: 1, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(200,0,0,0.8)' }, p: '1px' }}>
                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                {images.length < MAX_IMAGES_PER_PART && (() => {
                    const isDraggingOver = draggingPart === uploadKey;
                    return (
                    <Box
                        onClick={() => { if (!isUploading) document.getElementById(`file-input-${uploadKey}`)?.click(); }}
                        onDragEnter={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            dragCountRef.current[uploadKey] = (dragCountRef.current[uploadKey] || 0) + 1;
                            setDraggingPart(uploadKey);
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDragLeave={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            const next = Math.max(0, (dragCountRef.current[uploadKey] || 0) - 1);
                            dragCountRef.current[uploadKey] = next;
                            if (next === 0) setDraggingPart(prev => prev === uploadKey ? null : prev);
                        }}
                        onDrop={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            dragCountRef.current[uploadKey] = 0;
                            setDraggingPart(null);
                            if (!isUploading && e.dataTransfer.files?.length) handleImageUpload(partLabel, e.dataTransfer.files);
                        }}
                        sx={{
                            border: isDraggingOver ? '2px dashed' : '1px dashed',
                            borderColor: isDraggingOver ? colors.blueAccent[400] : colors.grey[600],
                            borderRadius: 1,
                            px: 2, py: 3, minHeight: 88,
                            textAlign: 'center', cursor: isUploading ? 'wait' : 'pointer',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            bgcolor: isDraggingOver ? `${colors.blueAccent[600]}1F` : 'transparent',
                            transform: isDraggingOver ? 'scale(1.01)' : 'scale(1)',
                            '&:hover': { borderColor: colors.blueAccent[400], bgcolor: `${colors.blueAccent[600]}0F` },
                            transition: 'all 0.15s',
                        }}
                    >
                        {isUploading ? <CircularProgress size={22} /> : (
                            <>
                                <CloudUploadIcon sx={{ fontSize: 28, color: isDraggingOver ? colors.blueAccent[400] : 'text.secondary' }} />
                                <Typography variant="body2" color={isDraggingOver ? colors.blueAccent[400] : 'text.secondary'}>
                                    {isDraggingOver
                                        ? 'Drop to upload'
                                        : `Click or drag images here (${images.length}/${MAX_IMAGES_PER_PART}) — JPG, PNG, HEIC, WebP`}
                                </Typography>
                            </>
                        )}
                        <input id={`file-input-${uploadKey}`} type="file" hidden multiple accept="image/jpeg,image/png,image/heic,image/heif,image/webp,.heic,.heif"
                            onChange={(e) => { if (e.target.files?.length) { handleImageUpload(partLabel, e.target.files); e.target.value = ''; } }}
                        />
                    </Box>
                    );
                })()}
            </Box>
        );
    };

    // Server pre-nests sub-parts as .subparts — walk that tree directly
    const getSubparts = (part) => part.subparts || part.v2_question_parts || part.children || [];

    // ─── Render: Upload Parts Tree (hierarchical) ─────────────
    function renderUploadPartsTree(parts, depth = 0, prefix = '') {
        if (!parts || parts.length === 0) return null;
        return parts.map(part => {
            const subs = getSubparts(part);
            const hasChildren = subs.length > 0;
            const compositeLabel = prefix + part.part_label;
            return (
                <Box key={part.id || compositeLabel} sx={{ ml: depth * 2, mt: depth === 0 ? 1 : 0.5 }}>
                    <Typography variant="body2" fontWeight={500} mb={0.25}>
                        Part {part.part_label}
                        {!hasChildren && ` (${part.max_points} pt${part.max_points !== 1 ? 's' : ''})`}
                    </Typography>
                    {!hasChildren && renderUploadDropzone(compositeLabel)}
                    {hasChildren && renderUploadPartsTree(subs, depth + 1, compositeLabel + '.')}
                </Box>
            );
        });
    }

    // ─── Render: Text Parts Tree (hierarchical) ───────────────
    function renderTextPartsTree(parts, depth = 0, prefix = '') {
        if (!parts || parts.length === 0) return null;
        let partAnswers = {};
        try { partAnswers = answer.answer_text ? JSON.parse(answer.answer_text) : {}; } catch { /* */ }

        return parts.map(part => {
            const subs = getSubparts(part);
            const hasChildren = subs.length > 0;
            const compositeLabel = prefix + part.part_label;
            const partText = partAnswers[compositeLabel] || '';
            return (
                <Box key={part.id || compositeLabel} sx={{ ml: depth * 2, mt: depth === 0 ? 1 : 0.5 }}>
                    <Typography variant="body2" fontWeight={500} mb={0.25}>
                        Part {part.part_label}
                        {!hasChildren && ` (${part.max_points} pt${part.max_points !== 1 ? 's' : ''})`}
                    </Typography>
                    {!hasChildren && (
                        <TextField fullWidth multiline minRows={3} variant="outlined"
                            placeholder={`Answer for part ${part.part_label}`}
                            value={partText}
                            onChange={(e) => {
                                const updated = { ...partAnswers, [compositeLabel]: e.target.value };
                                setAnswer(question.id, JSON.stringify(updated));
                            }}
                            inputProps={{ style: { resize: 'vertical' } }}
                        />
                    )}
                    {hasChildren && renderTextPartsTree(subs, depth + 1, compositeLabel + '.')}
                </Box>
            );
        });
    }

    // ─── Render: FRQ Parts ────────────────────────────────────
    const isUploadMode = !readOnly && frqSubmissionMethod === 'upload';

    const renderFrq = () => {
        const parts = question.v2_question_parts || [];

        if (readOnly) {
            let frqAnswers = {};
            let isPlainText = false;
            try { frqAnswers = answer.answer_text ? JSON.parse(answer.answer_text) : {}; }
            catch {
                isPlainText = true;
                frqAnswers = answer.answer_text ? { _raw: answer.answer_text } : {};
            }

            // Zero-part FRQ (LEQ, holistic DBQ). Render answer + whole-question score/feedback.
            if (parts.length === 0) {
                const ps = wholeQuestionScoreByQid[question.id];
                const answerText = isPlainText
                    ? frqAnswers._raw
                    : (typeof frqAnswers === 'string'
                        ? frqAnswers
                        : (Object.values(frqAnswers).find(v => typeof v === 'string') || ''));
                const answerImages = Array.isArray(frqAnswers.main) ? frqAnswers.main : [];

                return (
                    <Box sx={{ mt: 1 }}>
                        {answerText && (
                            <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">{isAdmin ? "Student's response:" : "Your response:"}</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{answerText}</Typography>
                            </Box>
                        )}
                        {answerImages.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">{isAdmin ? "Student's uploads:" : "Your uploads:"}</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                    {answerImages.map((url, idx) => (
                                        <Box key={idx} sx={{ width: 90, height: 90, borderRadius: 0.5, overflow: 'hidden', border: '1px solid', borderColor: colors.grey[700], cursor: 'pointer' }}
                                            onClick={() => window.open(url, '_blank')}>
                                            <img src={url} alt={`Upload ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {ps && (() => {
                            // Whole-question (partless) FRQ score — same edit mode as the
                            // parts tree. This branch previously rendered the Edit button
                            // but never read editingPart, so clicking did nothing visible.
                            const isEditingWhole = editingPart != null && editingPart.id === ps.id;
                            return (
                            <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: colors.grey[800], borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight={500}>Score</Typography>
                                    {isEditingWhole ? (
                                        <>
                                            <TextField size="small" type="number" value={editingPart?.score ?? ''}
                                                onChange={(e) => setEditingPart(p => ({ ...p, score: e.target.value }))} sx={{ width: 60 }}
                                            />
                                            <Typography variant="body2">/ {ps.max_score}</Typography>
                                            <IconButton size="small" onClick={onSavePartScore}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setEditingPart(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body2">
                                                {ps.grading_status === 'pending' || ps.grading_status === 'grading' ? '—' : `${ps.score ?? '?'}/${ps.max_score}`}
                                            </Typography>
                                            {ps.grading_status !== 'pending' && ps.grading_status !== 'grading' && ps.score != null && (
                                                ps.score >= ps.max_score ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> :
                                                ps.score > 0 ? <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} /> :
                                                <CancelIcon color="error" sx={{ fontSize: 16 }} />
                                            )}
                                            {isAdmin && ps.grading_status !== 'pending' && ps.grading_status !== 'grading' && (
                                                <IconButton size="small" onClick={() => setEditingPart && setEditingPart({ id: ps.id, score: ps.score ?? 0, feedback_text: ps.feedback_text || '' })}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                </Box>
                                {isEditingWhole && (
                                    <TextField fullWidth size="small" multiline rows={2} label="Feedback"
                                        value={editingPart?.feedback_text ?? ''} onChange={(e) => setEditingPart(p => ({ ...p, feedback_text: e.target.value }))} sx={{ mt: 1 }}
                                    />
                                )}
                                {!isEditingWhole && ps.feedback_text && (
                                    <Box sx={{ mt: 0.5, p: 1, borderLeft: '3px solid', borderColor: colors.blueAccent[400], bgcolor: 'action.hover', borderRadius: '0 4px 4px 0' }}>
                                        <Typography variant="caption" color="text.secondary">Feedback:</Typography>
                                        <QuestionRender content={ps.feedback_text} sx={{ '& p': { margin: 0, whiteSpace: 'pre-wrap' } }} />
                                    </Box>
                                )}
                            </Box>
                            );
                        })()}
                    </Box>
                );
            }

            // Parts-based FRQ. If the stored answer isn't JSON-keyed per part
            // (legacy plain text, or the student wrote a single narrative answer
            // before parts were added), show it once up top and still render the
            // parts tree so per-part scores + feedback appear.
            return (
                <Box sx={{ mt: 1 }}>
                    {frqAnswers._raw && (
                        <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">{isAdmin ? "Student's response:" : "Your response:"}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{frqAnswers._raw}</Typography>
                        </Box>
                    )}
                    {renderPartsTree(buildPartTree(parts), 0, frqAnswers)}
                </Box>
            );
        }

        // Also render uploaded images in readOnly results for image answers
        // (handled by renderPartsTree — images stored as arrays will display as text for now)

        // Upload mode — no parts
        if (isUploadMode && parts.length === 0) {
            return renderUploadDropzone('main');
        }

        if (parts.length === 0) {
            return (
                <TextField fullWidth multiline minRows={4} variant="outlined" placeholder="Type your response"
                    value={answer.answer_text || ''} onChange={(e) => setAnswer(question.id, e.target.value)}
                    sx={{ mt: 1 }} inputProps={{ style: { resize: 'vertical' } }}
                />
            );
        }

        // Upload mode — hierarchical dropzones
        if (isUploadMode) {
            return (
                <Box sx={{ mt: 1 }}>
                    {renderUploadPartsTree(parts, 0)}
                </Box>
            );
        }

        // Text mode — hierarchical text fields
        return (
            <Box sx={{ mt: 1 }}>
                {renderTextPartsTree(parts, 0)}
            </Box>
        );
    };

    // ─── FRQ Results: Hierarchical Part Tree ──────────────────
    function buildPartTree(flatParts) {
        if (!flatParts || flatParts.length === 0) return [];
        const byId = {};
        flatParts.forEach(p => { byId[p.id] = { ...p, children: [] }; });
        const roots = [];
        flatParts.forEach(p => {
            if (p.parent_id && byId[p.parent_id]) byId[p.parent_id].children.push(byId[p.id]);
            else roots.push(byId[p.id]);
        });
        const sortParts = (list) => { list.sort((a, b) => (a.part_order || 0) - (b.part_order || 0)); list.forEach(p => sortParts(p.children)); };
        sortParts(roots);
        return roots;
    }

    function renderPartsTree(parts, depth = 0, frqAnswers = {}, prefix = '') {
        if (!parts || parts.length === 0) return null;
        return parts.map(part => {
            const subs = getSubparts(part);
            const hasChildren = subs.length > 0;
            const compositeLabel = prefix + part.part_label;
            const ps = partScoreById[part.id];
            const isEditing = ps != null && editingPart != null && editingPart.id === ps.id;
            const studentText = frqAnswers[compositeLabel] ?? frqAnswers[part.part_label] ?? '';

            return (
                <Box key={part.id} sx={{ ml: depth * 2, mt: 0.5 }}>
                    <Box sx={{ p: 1, border: '1px solid', borderColor: colors.grey[800], borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                                Part {part.part_label}{!hasChildren && ':'}
                            </Typography>
                            {!hasChildren && ps && (
                                isEditing ? (
                                    <>
                                        <TextField size="small" type="number" value={editingPart?.score ?? ''}
                                            onChange={(e) => setEditingPart(p => ({ ...p, score: e.target.value }))} sx={{ width: 60 }}
                                        />
                                        <Typography variant="body2">/ {ps.max_score}</Typography>
                                        <IconButton size="small" onClick={onSavePartScore}><SaveIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => setEditingPart(null)}><CloseIcon fontSize="small" /></IconButton>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2">
                                            {ps.grading_status === 'pending' || ps.grading_status === 'grading' ? '—' : `${ps.score ?? '?'}/${ps.max_score}`}
                                        </Typography>
                                        {ps.grading_status !== 'pending' && ps.grading_status !== 'grading' && ps.score != null && (
                                            ps.score >= ps.max_score ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> :
                                            ps.score > 0 ? <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} /> :
                                            <CancelIcon color="error" sx={{ fontSize: 16 }} />
                                        )}
                                        {isAdmin && ps.grading_status !== 'pending' && ps.grading_status !== 'grading' && (
                                            <IconButton size="small" onClick={() => setEditingPart({ id: ps.id, score: ps.score ?? 0, feedback_text: ps.feedback_text || '' })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </>
                                )
                            )}
                        </Box>
                        {studentText && (
                            <Box sx={{ mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">{isAdmin ? "Student's response:" : "Your response:"}</Typography>
                                {Array.isArray(studentText) ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                        {studentText.map((url, idx) => (
                                            <Box key={idx} sx={{ width: 90, height: 90, borderRadius: 0.5, overflow: 'hidden', border: '1px solid', borderColor: colors.grey[700], cursor: 'pointer' }}
                                                onClick={() => window.open(url, '_blank')}>
                                                <img src={url} alt={`Upload ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{studentText}</Typography>
                                )}
                            </Box>
                        )}
                        {!hasChildren && part.rubric_text && (
                            <Box sx={{ mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Rubric:</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{part.rubric_text}</Typography>
                            </Box>
                        )}
                        {isEditing && (
                            <TextField fullWidth size="small" multiline rows={2} label="Feedback"
                                value={editingPart?.feedback_text ?? ''} onChange={(e) => setEditingPart(p => ({ ...p, feedback_text: e.target.value }))} sx={{ mt: 1 }}
                            />
                        )}
                        {!isEditing && ps?.feedback_text && (
                            <Box sx={{ mt: 0.5, p: 1, borderLeft: '3px solid', borderColor: colors.blueAccent[400], bgcolor: 'action.hover', borderRadius: '0 4px 4px 0' }}>
                                <Typography variant="caption" color="text.secondary">Feedback:</Typography>
                                <QuestionRender content={ps.feedback_text} sx={{ '& p': { margin: 0, whiteSpace: 'pre-wrap' } }} />
                            </Box>
                        )}
                    </Box>
                    {hasChildren && renderPartsTree(subs, depth + 1, frqAnswers, compositeLabel + '.')}
                </Box>
            );
        });
    }

    // ─── Main Render ──────────────────────────────────────────
    const renderContent = () => {
        if (isMcqType && questionType === 'mcq') return renderMcq();
        if (questionType === 'multi_answer' || questionType === 'grid_in' || questionType === 'sa') {
            return renderTextInput();
        }
        return renderFrq();
    };

    // In readOnly (results) mode with persisted selections, hide non-selected
    // choice-group questions entirely — they weren't answered and weren't graded.
    if (readOnly && isHiddenByGroup) return null;

    return (
        <>
            {renderChoiceGroupSelector()}
            <Box
                id={`question-${question.id}`}
                sx={{
                    p: 2, mb: noBorder ? 0 : 2, borderRadius: noBorder ? 0 : 1,
                    border: noBorder ? 'none' : '1px solid',
                    borderColor: noBorder ? 'transparent' : (answer.flagged ? '#f59e0b' : colors.grey[700]),
                    bgcolor: isHiddenByGroup ? `${colors.grey[800]}60` : 'transparent',
                    opacity: isHiddenByGroup ? 0.4 : 1,
                    pointerEvents: isHiddenByGroup ? 'none' : 'auto',
                    transition: 'all 0.15s',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                        Q{question.question_number || '?'}
                        <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                            {questionType === 'mcq' ? 'MCQ' :
                             questionType === 'multi_answer' ? 'Multi-Answer' :
                             questionType === 'grid_in' ? 'Grid-In' :
                             (questionType || '').toUpperCase()}
                            {question.max_points > 1 && ` (${question.max_points} pts)`}
                        </Typography>
                    </Typography>
                    {readOnly ? (
                        answer.flagged && (
                            <Tooltip title="Flagged for review">
                                <FlagIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                            </Tooltip>
                        )
                    ) : (
                        <FlagButton flagged={answer.flagged} onToggle={() => toggleFlag(question.id)} />
                    )}
                </Box>
                {renderContent()}
            </Box>
        </>
    );
};

export default QuestionSlot;
